import { getPhoneData } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { isAuthError, type User } from '@supabase/supabase-js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useSpinDelay } from 'spin-delay'
import { OtpVerification } from '~/components/auth/OtpVerification'
import { useAlert } from '~/components/GlobalAlert'
import { Body, Label } from '~/components/typography/Content'
import { Button } from '~/components/ui/button'
import { Input, type InputVariant } from '~/components/ui/input'
import { useSession } from '~/components/user/SessionProvider'
import { sendAuthContactOtp } from '~/lib/auth/contactOtp'
import { useChangeAuthContact, useResendOtp, useVerifyOtp } from '~/lib/auth/useOtpAuth'
import { useOtpStateManager } from '~/lib/auth/useOtpStateManager'
import { cn } from '~/lib/utils'

type ContactType = 'email' | 'phone'

export function UserAuthField({
  contactType,
  contactValue,
  className,
  inputVariant = 'box',
  disabled = false,
  onVerify,
  onError,
  label,
}: {
  contactType: ContactType
  contactValue?: string
  className?: string
  inputVariant?: InputVariant
  disabled?: boolean
  onVerify?: (user: User | null) => void
  onError?: (error: unknown) => void
  label?: string | false
}) {
  const { t } = useTranslations('user')
  const { showAlert } = useAlert()
  const { session } = useSession()
  const user = session?.user

  const isEmail = contactType === 'email'
  const sessionContactValue = isEmail ? user?.email : user?.phone
  const pendingFromSession = isEmail ? user?.new_email : user?.new_phone

  const [internalContactValue, setInternalContactValue] = useState<string>(
    contactValue ?? sessionContactValue ?? ''
  )
  const [pendingChange, setPendingChange] = useState<string | null>(null)
  const [otp, setOtp] = useState('')

  const otpStateManager = useOtpStateManager()
  const changeAuthContact = useChangeAuthContact()
  const resendOtpMutation = useResendOtp()
  const verifyOtpMutation = useVerifyOtp()

  useEffect(() => {
    setPendingChange((current) => {
      if (!current && pendingFromSession) {
        return pendingFromSession
      } else if (!pendingFromSession && current) {
        return null
      }

      return current
    })
  }, [pendingFromSession])

  const resolvedLabel = useMemo(() => {
    if (label === false) {
      return undefined
    }

    if (typeof label === 'string') {
      return label
    }

    return isEmail
      ? t(($) => $.contactMethods.authField.emailLabel)
      : t(($) => $.contactMethods.authField.phoneLabel)
  }, [isEmail, label, t])

  const normalizedContactValue = useMemo(() => {
    if (!internalContactValue) {
      return undefined
    }

    if (isEmail) {
      return internalContactValue.trim()
    }

    return getPhoneData(internalContactValue)?.phoneNumber ?? undefined
  }, [internalContactValue, isEmail])

  const isValid = useMemo(() => {
    if (disabled || !normalizedContactValue) {
      return false
    }

    if (isEmail) {
      const trimmed = normalizedContactValue.trim()
      return !!trimmed && /@/.test(trimmed)
    }

    const data = getPhoneData(normalizedContactValue)
    return data?.isPossible ?? false
  }, [disabled, normalizedContactValue, isEmail])

  const isChanged = useMemo(() => {
    if (!normalizedContactValue) {
      return false
    }

    if (isEmail) {
      return normalizedContactValue !== sessionContactValue
    }

    const sessionPhone = sessionContactValue
      ? getPhoneData(sessionContactValue)?.phoneNumber
      : undefined
    return normalizedContactValue !== sessionPhone
  }, [normalizedContactValue, sessionContactValue, isEmail])

  const sendOtp = useCallback(
    async (value: string) => {
      return sendAuthContactOtp({
        contactType: isEmail ? 'email' : 'phone',
        contactValue: value,
        changeAuthContact,
        otpStateManager,
        onError,
      })
    },
    [isEmail, changeAuthContact, otpStateManager, onError]
  )

  const handleStart = useCallback(async () => {
    if (!isValid || disabled || !normalizedContactValue) {
      return
    }

    setInternalContactValue(normalizedContactValue)
    setPendingChange(normalizedContactValue)
    setOtp('')

    try {
      const success = await sendOtp(normalizedContactValue)

      if (!success) {
        setPendingChange(null)

        showAlert({
          type: 'error',
          title: t(($) => $.contactMethods.error.title),
          description: t(($) => $.contactMethods.error.contactCannotBeUsed),
        })
      }
    } catch (error) {
      setPendingChange(null)

      if (isAuthError(error) && (error.code === 'phone_exists' || error.code === 'email_exists')) {
        showAlert({
          type: 'error',
          title: t(($) => $.contactMethods.error.title),
          description: isEmail
            ? t(($) => $.contactMethods.error.emailExists)
            : t(($) => $.contactMethods.error.phoneExists),
        })
      } else {
        showAlert({
          type: 'error',
          title: t(($) => $.contactMethods.error.title),
          description: t(($) => $.contactMethods.error.contactCannotBeUsed),
        })

        onError?.(error)
      }
    }
  }, [isValid, disabled, normalizedContactValue, sendOtp, showAlert, isEmail, onError, t])

  const handleResendOtp = useCallback(async () => {
    if (!pendingChange || !otpStateManager.canResendOtp(pendingChange)) {
      return
    }

    try {
      const resendProps = isEmail
        ? { email: pendingChange, type: 'email_change' as const }
        : { phone: pendingChange, type: 'phone_change' as const }

      const result = await resendOtpMutation.mutateAsync(resendProps)

      if (result) {
        otpStateManager.markOtpSent(pendingChange)
      }
    } catch (error) {
      console.error('Failed to resend OTP:', error)
      onError?.(error)
    }
  }, [pendingChange, isEmail, otpStateManager, resendOtpMutation, onError])

  const handleResetChange = useCallback(async () => {
    showAlert({
      type: 'error',
      title: t(($) => $.contactMethods.authField.resetChangeTitle),
      description: t(($) => $.contactMethods.authField.resetChangeDescription),
    })
  }, [showAlert, t])

  const handleVerifyOtp = useCallback(async () => {
    if (!pendingChange || otp.length !== 6) {
      return
    }

    try {
      const verifyProps = isEmail
        ? { email: pendingChange, token: otp, type: 'email_change' as const }
        : { phone: pendingChange, token: otp, type: 'phone_change' as const }

      const verifiedUser = await verifyOtpMutation.mutateAsync(verifyProps)

      if (!verifiedUser) {
        // eslint-disable-next-line i18next/no-literal-string
        onError?.(new Error('Verification failed'))
        return
      }

      const nextContact = isEmail
        ? (verifiedUser.email?.trim() ?? '')
        : (getPhoneData(verifiedUser.phone ?? '')?.phoneNumber ?? verifiedUser.phone ?? '')

      setInternalContactValue(nextContact)
      setPendingChange(null)
      setOtp('')

      onVerify?.(verifiedUser)
    } catch (error) {
      console.error('Failed to verify OTP:', error)
      onError?.(error)
    }
  }, [pendingChange, otp, isEmail, verifyOtpMutation, onVerify, onError])

  const started = useSpinDelay(!!pendingChange, {
    delay: 150,
    minDuration: 3000,
  })

  const isOtpComplete = otp.length === 6

  const isLoading =
    changeAuthContact.isPending || resendOtpMutation.isPending || verifyOtpMutation.isPending

  return (
    <View className={cn('gap-3', className)}>
      {!started ? (
        <View className="gap-4">
          {resolvedLabel && <Label>{resolvedLabel}</Label>}
          {isEmail ? (
            <Input
              value={internalContactValue}
              onChangeText={setInternalContactValue}
              placeholder={t(($) => $.contactMethods.authField.emailPlaceholder)}
              autoCapitalize="none"
              autoComplete="email"
              keyboardType="email-address"
              variant={inputVariant}
              editable={!disabled}
              className={cn(disabled && 'pointer-events-none opacity-50')}
            />
          ) : (
            <Input
              type="phone"
              value={internalContactValue}
              onChangeText={(display, _, stored) => setInternalContactValue(stored ?? display)}
              placeholder={t(($) => $.contactMethods.authField.phonePlaceholder)}
              autoCapitalize="none"
              // eslint-disable-next-line i18next/no-literal-string
              autoComplete="tel"
              keyboardType="phone-pad"
              variant={inputVariant}
              editable={!disabled}
              className={cn(disabled && 'pointer-events-none opacity-50')}
            />
          )}
          {isValid && isChanged && (
            <Button
              className="self-center"
              onPress={handleStart}
              disabled={isLoading}
              variant="secondary">
              {t(($) => $.contactMethods.authField.sendCode)}
            </Button>
          )}
        </View>
      ) : (
        <View className="gap-4">
          <View className="gap-2">
            <Body className="text-center text-sm">
              {isEmail
                ? t(($) => $.contactMethods.authField.codeSentToEmail)
                : t(($) => $.contactMethods.authField.codeSentToPhone)}{' '}
            </Body>
            <Body className="text-center font-medium">{pendingChange}</Body>
          </View>

          <OtpVerification
            otp={otp}
            setOtp={setOtp}
            onResend={handleResendOtp}
            onReset={handleResetChange}
            resetLabel={t(($) => $.contactMethods.authField.resetChange)}
            otpStateManager={otpStateManager}
            pendingChange={pendingChange}
          />

          <Button
            className="self-center"
            onPress={handleVerifyOtp}
            disabled={!isOtpComplete || isLoading}
            variant="primary">
            {t(($) => $.contactMethods.authField.verify)}
          </Button>
        </View>
      )}
    </View>
  )
}
